/**
 * Processing-site phone strings (English), spread into en.ts. Hindi: process.hi.ts (typed to have
 * every key here). Plurals use `{n:one|other}` (i18n/plural.ts).
 */
export const processEn = {
  'process.devSeed': 'Load processing site demo data',
  'process.home.section': 'Processing',
  'process.saveFailed': 'Could not save on the phone',
  'process.remove': 'Remove',
  'process.lot': 'Lot',
  'process.farmer': 'Farmer',
  'process.moisture': 'Moisture (%)',
  'process.bagsIn': '{n} {n:bag|bags} in',
  'process.sacksN': '{n} {n:sack|sacks}',
  'process.typeAgain': 'Type the weight again',
  'process.typeAgainHint':
    'This weight is outside the usual range for one bag, so it is typed twice.',
  'process.typeAgainMismatch': 'The two weights do not match',

  'process.cache.update': 'Update site list',
  'process.cache.updated': 'Site list updated',
  'process.cache.failed': 'Could not update the site list',
  'process.cache.needsNetwork': 'Needs network to update',
  'process.cache.age': 'Site list from {when}',
  'process.cache.stale': 'The site list is more than a day old',
  'process.cache.expired': 'The site list is too old — update it before you work',
  'process.cache.missingTitle': 'No site list on this phone',
  'process.cache.missingBody':
    'Download the site list once with network: machines, runs and bags work offline after that.',

  'process.badge.notSynced': 'Not sent yet',
  'process.badge.heldAtOffice': 'Held at office',
  'process.badge.syncProblem': 'Sync problem',
  'process.stream.MAIN': 'Brand',
  'process.stream.CONV': 'Ordinary',
  'process.status.OPEN': 'Open',
  'process.status.RUNNING': 'Running',
  'process.status.CLOSED': 'Closed',
  'process.status.BALANCED': 'Balanced',
  'process.status.EXCEPTION': 'Held',
  'process.status.CANCELLED': 'Cancelled',

  // runs list
  'process.runs.title': 'Machine runs',
  'process.runs.hint': 'Open a run, clean, weigh in and out, close',
  'process.runs.open': 'Open runs · {n}',
  'process.runs.noneOpen': 'No run is open at this site.',
  'process.runs.recent': 'Recently closed',
  'process.runs.sampleDue': '{n} brand {n:batch needs|batches need} a QC sample.',
  'process.runs.flagged': '{n} {n:run is|runs are} held for a check.',

  // open run
  'process.open.title': 'Open run',
  'process.open.machine': 'Machine label',
  'process.open.step': 'Step',
  'process.open.scanLot': 'Scan a bag of the lot',
  'process.open.addLot': 'Add another lot of the same farmer',
  'process.open.lots': 'Lots for this run',
  'process.open.check': 'Check before opening',
  'process.open.runType': 'Run type',
  'process.open.planned': 'Kilos planned',
  'process.open.plannedOfMin': '{kg} (minimum {min})',
  'process.open.previous': 'Previous run on this machine',
  'process.open.previousUnknown': 'Unknown (shared machine)',
  'process.open.flush': 'Flush',
  'process.open.flushNeeded': 'Needed: at least {kg}',
  'process.open.allergen': 'Allergen clean',
  'process.open.allergenNeeded': 'Needed: crop changed or unknown',
  'process.open.notNeeded': 'Not needed',
  'process.open.belowMinTitle': 'Lot is smaller than this machine’s minimum',
  'process.open.belowMinBody':
    'This machine needs at least {min} for a brand run. Scan a bag of another lot of the same farmer, crop and season, or stop and call the office.',
  'process.open.offlineNote':
    'Opened without network: a flush and a full allergen clean are needed.',
  'process.open.done': 'Run {code} opened',

  // cancel
  'process.cancel.button': 'Cancel run',
  'process.cancel.hint': 'Only before any bag has gone in.',
  'process.cancel.confirm': 'Cancel this run',
  'process.cancel.reason.WRONG_MACHINE': 'Wrong machine scanned',
  'process.cancel.reason.WRONG_LOT': 'Wrong lot scanned',
  'process.cancel.reason.OTHER': 'Other reason',
  'process.cancel.done': 'Run cancelled',

  // run screen
  'process.run.title': 'Run',
  'process.run.missing': 'This run is not on the phone',
  'process.run.missingBody': 'Update the site list with network.',
  'process.run.startedAt': 'started {time}',
  'process.run.syncError': 'Something on this run could not be sent. Open Sync to see it.',
  'process.run.sampleNow':
    'Take the QC sample of this run’s brand batch before its sacks leave the machine area.',
  'process.tab.cleanout': 'Clean',
  'process.tab.in': 'In',
  'process.tab.run': 'Run',
  'process.tab.out': 'Out',

  // cleanout + flush
  'process.cleanout.title': 'Cleanout + flush',
  'process.cleanout.intro':
    'Empty the machine completely. Anything left inside goes in the flush sack, never in a brand sack.',
  'process.cleanout.allergenTitle': 'Full allergen clean',
  'process.cleanout.allergenWhyCrop':
    'The last run on this machine was {crop}. Brush and blow out the whole machine and wipe the chutes.',
  'process.cleanout.allergenWhyUnknown':
    'The last crop on this machine is not known. Brush and blow out the whole machine and wipe the chutes.',
  'process.cleanout.allergenPhoto': 'Photo of the whole machine, clean',
  'process.cleanout.allergenDone': 'Allergen clean done',
  'process.cleanout.photosTitle': 'Machine empty',
  'process.cleanout.empty': 'Machine empty',
  'process.cleanout.emptyHint':
    'Take every photo first (and the allergen clean when it is needed).',
  'process.cleanout.confirm': 'Confirm cleanout',
  'process.cleanout.confirmed': 'Cleanout confirmed',
  'process.cleanout.saved': 'Cleanout saved',
  'process.flush.title': 'Flush sack',
  'process.flush.afterCleanout':
    'After the first bag is tipped, catch everything that comes out in a flush sack until it holds at least {kg}. It is always ordinary: red tie.',
  'process.flush.notNeeded': 'No flush needed for this run.',
  'process.flush.more': '{kg} more flush needed',
  'process.flush.met': 'Flush done: {kg}',

  // weigh in
  'process.weighIn.title': 'Weigh in',
  'process.weighIn.locked': 'Weigh in is locked',
  'process.weighIn.lockedBody': 'Confirm the cleanout first; no bag goes in before it.',
  'process.weighIn.scan': 'Scan bag at the hopper',
  'process.weighIn.bag': 'Bag',
  'process.weighIn.issueWeight': 'Weight from issue',
  'process.weighIn.notSeen':
    'This phone has not seen this bag being issued. Put it on the scale and type the weight; the office checks it later.',
  'process.weighIn.weight': 'Weight (kg)',
  'process.weighIn.weightHint': 'What the scale shows, with the bag',
  'process.weighIn.bagIn': 'Bag in',
  'process.weighIn.tinHint': 'Tip the bag in, cut off the tag and drop it in the destroy tin.',
  'process.weighIn.saved': '{tag} is in',
  'process.weighIn.bagsIn': 'Bags in',
  'process.weighIn.kgIn': 'Kilos in',
  'process.weighIn.waiting': 'Issued, not in yet · {n}',
  'process.weighIn.inList': 'Bags in',
  'process.weighIn.weighedHere': 'weighed here',

  // run in progress
  'process.running.title': 'Run in progress',
  'process.running.started': 'Started',
  'process.running.notStarted': 'No bag in yet',
  'process.running.inSoFar': 'Bags in / kilos in',
  'process.running.settings': 'Settings',
  'process.running.settingsHint': 'Sent with Close run.',
  'process.running.sweepHint':
    'When the last material is through, empty the machine and sweep the sweepings into the waste sack.',
  'process.running.stopped': 'Machine stopped',
  'process.running.stoppedAt': 'Machine stopped at {time}',
  'process.running.undoStop': 'Undo',
  'process.mixing.button': 'Report mixing',
  'process.mixing.hint':
    'The whole run becomes ordinary at once, including sacks already tagged. Every later sack gets a red tie.',
  'process.mixing.confirm': 'Report mixing',
  'process.mixing.saved': 'Mixing reported: this run is now ordinary',
  'process.mixing.reportedTitle': 'Mixing reported',
  'process.mixing.reportedBody': 'This run is ordinary produce. Tie a red tie on every sack.',
  'process.mixing.reason.RAIN': 'Rain on the yard',
  'process.mixing.reason.SPILL': 'Spill',
  'process.mixing.reason.OTHER_FARMER': 'Other farmer’s material',
  'process.mixing.reason.MACHINE_NOT_EMPTY': 'Machine not empty',
  'process.mixing.reason.OTHER': 'Other',

  // weigh out
  'process.weighOut.title': 'Weigh out',
  'process.weighOut.output': 'Output',
  'process.weighOut.newSack': 'New sack',
  'process.weighOut.tag': 'Sack tag',
  'process.weighOut.sackType': 'Sack type',
  'process.weighOut.weight': 'Weight (kg)',
  'process.weighOut.weightHint': 'What the scale shows; the sack ({tare}) is taken off',
  'process.weighOut.net': 'After the sack: {kg}',
  'process.weighOut.save': 'Save sack',
  'process.weighOut.saved': '{name}: {kg} saved',
  'process.weighOut.byOutput': 'Sacks by output',
  'process.weighOut.none': 'Nothing from this output',
  'process.weighOut.zero': 'None',
  'process.weighOut.void': 'Void this sack',
  'process.weighOut.voided': 'Sack voided',
  'process.void.WRONG_OUTPUT': 'Wrong output tapped',
  'process.void.WRONG_WEIGHT': 'Wrong weight typed',
  'process.void.TAG_PROBLEM': 'Tag problem',
  'process.void.OTHER': 'Other',
  'process.output.FLUSH': 'Flush',
  'process.output.LEFTOVER': 'Leftover',
  'process.output.SPILLAGE': 'Spillage',
  'process.tie.red': 'Red tie',
  'process.tie.redShort': 'Ordinary',
  'process.tie.none': 'No tie',
  'process.tie.redBanner': 'Ordinary produce: tie a red tie on this sack',

  // close
  'process.close.button': 'Close run',
  'process.close.notYet': 'Close run needs',
  'process.close.done': 'Run closed. The result shows after sync.',
  'process.close.gap.NO_CLEANOUT': 'Cleanout confirmed',
  'process.close.gap.NO_INPUT': 'At least one bag in',
  'process.close.gap.FLUSH_SHORT': '{kg} more flush',
  'process.close.gap.OUTPUT_MISSING': '{name}: a sack, or None',
  'process.close.gap.BAGS_NOT_IN': '{n} issued {n:bag|bags} scanned in, or put back by the store',
  'process.close.gap.NOT_STOPPED': 'Machine stopped',

  // balance
  'process.balance.provisional': 'Totals · provisional until sync',
  'process.balance.synced': 'Totals · checked by the office',
  'process.balance.provisionalHint':
    'Worked out on this phone. The office’s check replaces it after sync.',
  'process.balance.pass': 'Adds up',
  'process.balance.held': 'Will be held',
  'process.balance.in': 'Kilos in',
  'process.balance.flush': 'Flush',
  'process.balance.product': 'Product',
  'process.balance.byproduct': 'By-product',
  'process.balance.waste': 'Waste',
  'process.balance.leftOver': 'Left over (limit {limit})',
  'process.balance.yield': 'Product yield',
  'process.balance.yieldRange': 'Product yield ({min} – {max})',
  'process.balance.kind.RESIDUAL': 'Loss above the limit',
  'process.balance.kind.YIELD': 'Yield outside the usual range',
  'process.balance.kind.SURPLUS': 'More out than in',
  'process.balance.kind.EXACT_ZERO': 'Exactly nothing left over',

  // after close
  'process.closed.cancelled': 'This run was cancelled.',
  'process.closed.waitingTitle': 'Closed on this phone',
  'process.closed.waitingBody': 'The office checks the balance when the phone syncs.',
  'process.closed.balancedTitle': 'Balanced',
  'process.closed.balancedBody': 'The sacks are free to be put away, issued or packed.',
  'process.closed.heldTitle': 'Held for a check',
  'process.closed.heldBody':
    'The sacks can be put away but not issued or packed until a supervisor closes the problem.',
  'process.closed.outputs': 'Sacks',
  'process.closed.samples': 'QC samples',
  'process.problem.BALANCE_OUT_OF_BAND': 'Weights do not add up',
  'process.problem.RUN_MIXED': 'Mixing',
  'process.problem.LATE_ENTRY': 'Late entry',
  'process.problem.BAG_COUNT_MISMATCH': 'Bag missing',
  'process.problem.TAG_CONFLICT': 'Tag used twice',
  'process.problem.TAG_REUSED': 'Used tag scanned',
  'process.problem.LABEL_MISMATCH': 'Label check',
  'process.problem.WEIGHT_OUT_OF_RANGE': 'Low pouch weights',
  'process.problem.LAB_FAIL': 'Lab fail',
  'process.problem.OTHER': 'Problem',

  // machine label
  'process.label.title': 'Machine label',
  'process.label.hint': 'Stick and bind a machine’s label',
  'process.label.scan': 'Machine label',
  'process.label.machine': 'Machine',
  'process.label.machineHint': 'Machines at this site with no label yet',
  'process.label.site': 'Site',
  'process.label.photo': 'Photo of label on machine',
  'process.label.bind': 'Bind label',
  'process.label.done': 'Label bound to {code}',
  'process.label.allLabelled': 'Every machine at this site already has a label.',

  // issue to run
  'process.issue.title': 'Issue to run',
  'process.issue.hint': 'Hand a lot’s bags to a run, weigh each bag',
  'process.issue.none': 'No issue task',
  'process.issue.noneBody':
    'The office sets each task (lot to run). Update the site list to see new ones.',
  'process.issue.task': 'Issue task',
  'process.issue.toRun': 'to run {run}',
  'process.issue.progress': '{n} of {m} bags',
  'process.issue.lots': 'Lots',
  'process.issue.run': 'Run',
  'process.issue.bags': 'Bags',
  'process.issue.kgSoFar': 'Kilos so far',
  'process.issue.scan': 'Scan bag on the scale',
  'process.issue.bagTag': 'Bag tag',
  'process.issue.sackType': 'Sack type',
  'process.issue.weight': 'Weight (kg)',
  'process.issue.weightHint': 'The bag alone on the scale',
  'process.issue.moisture': 'Moisture (%)',
  'process.issue.moistureOnce': 'Once per lot, if you have a meter',
  'process.issue.moistureNeeded': 'Needed once per lot for drying',
  'process.issue.moistureMissing': 'Type the moisture once for every lot before Issue done.',
  'process.issue.save': 'Save bag',
  'process.issue.saved': '{tag}: {kg} issued',
  'process.issue.carryHint':
    'Carry each bag to the machine area and put it with the run’s other bags.',
  'process.issue.done': 'Issue done',
  'process.issue.shortConfirm':
    '{n} {n:bag was|bags were} not found. They keep their own problem for the office.',
  'process.issue.closed': 'Issue done',
  'process.issue.otherTask': 'Other tasks',

  // QC sample
  'process.qc.title': 'QC sample',
  'process.qc.hint': 'Seal the sample of a brand batch for the lab',
  'process.qc.none': 'No batch needs a sample',
  'process.qc.noneBody': 'Only the brand batch of the last run before packing is sampled.',
  'process.qc.batch': 'Batch',
  'process.qc.sacks': 'Sacks to scoop from',
  'process.qc.howTo':
    'One scoop from every sack into a clean sample bag. Mix, fill to the mark and close it.',
  'process.qc.seal': 'Sample seal',
  'process.qc.sealHint': 'From this site’s sample seal range',
  'process.qc.weight': 'Sample weight (g)',
  'process.qc.photo': 'Photo of sealed sample',
  'process.qc.sealIt': 'Seal sample',
  'process.qc.done': 'Sample sealed with {seal}',
  'process.qc.dispatchNote': 'Hand it over for dispatch; the office records the lab and the date.',
  'process.qc.sealed': 'Sealed',
  'process.qc.confirming': 'Confirming sample',
  'process.qc.confirmingHint': 'Held batch: fresh sample asked for',
  'process.qc.confirmingBanner':
    'This batch is on hold. The quality team asked for a fresh sample to confirm the lab result.',

  // investigate
  'process.investigate.title': 'Investigate',
  'process.investigate.hint': 'Correct a held run and send it for a re-check',
  'process.investigate.none': 'No run is held',
  'process.investigate.noneBody': 'Held runs at this site show here after sync.',
  'process.investigate.flagged': 'Held runs',
  'process.investigate.ownTitle': 'Not allowed',
  'process.investigate.ownBody':
    'You ran or cleaned this machine for this run. A supervisor who did not must correct it.',
  'process.investigate.run': 'Run',
  'process.investigate.machine': 'Machine',
  'process.investigate.cause': 'Cause',
  'process.investigate.correction': 'Correction',
  'process.investigate.tag': 'Sack or bag tag',
  'process.investigate.weight': 'Corrected weight (kg)',
  'process.investigate.record': 'Record',
  'process.investigate.recorded': 'Corrections · {n}',
  'process.investigate.leftNow': 'Left over now',
  'process.investigate.recheck': 'Re-check',
  'process.investigate.noCorrectionHint':
    'With no correction to make, the office records the cause when it decides.',
  'process.investigate.sent': 'Corrections sent. The office re-checks the run.',
  'process.correction.REWEIGH_OUTPUT': 'Re-weigh a sack',
  'process.correction.ADD_OUTPUT': 'Add a missed sack',
  'process.correction.ADD_INPUT': 'Bag went in without a scan',
  'process.correction.VOID_OUTPUT': 'Void a sack',
  'process.cause.UNWEIGHED_SACK': 'Unweighed sack',
  'process.cause.SCALE_ERROR': 'Scale error',
  'process.cause.TYPING_ERROR': 'Typing error',
  'process.cause.BAG_IN_WITHOUT_SCAN': 'Bag in without scan',
  'process.cause.HIGH_MOISTURE': 'High moisture',
  'process.cause.MACHINE_FAULT': 'Machine fault',
  'process.cause.BAND_WRONG': 'Usual range wrong',
  'process.cause.UNEXPLAINED_LOSS': 'Unexplained loss',
  'process.cause.MIXING': 'Mixing',

  // refusals (shown in both languages)
  'process.refusal.TAG_UNKNOWN': 'Code not known on this phone',
  'process.refusal.TAG_WRONG_KIND': 'Wrong kind of tag',
  'process.refusal.TAG_VOID': 'This tag was voided',
  'process.refusal.TAG_USED': 'This tag is already used',
  'process.refusal.NOT_YOUR_ROLL': 'Not from your roll',
  'process.refusal.NOT_A_MACHINE_LABEL': 'Not a machine label',
  'process.refusal.MACHINE_HAS_LABEL': 'This machine already has a label',
  'process.refusal.MACHINE_BUSY': 'Run {code} is open on this machine — close or cancel it first',
  'process.refusal.MACHINE_OTHER_SITE': 'Machine of another site',
  'process.refusal.NO_SETTINGS': 'Machine not set up for this crop',
  'process.refusal.LOT_ON_HOLD': 'Lot on hold',
  'process.refusal.LOT_USED_UP': 'Lot already used up',
  'process.refusal.LOT_ELSEWHERE': 'Lot is not at this site',
  'process.refusal.OTHER_FARMER': 'Other farmer’s lot',
  'process.refusal.OTHER_CROP': 'Other crop',
  'process.refusal.OTHER_STREAM': 'Brand and ordinary cannot mix',
  'process.refusal.NOT_ON_TASK': 'Not a lot of this task — put it back',
  'process.refusal.NOT_ISSUED': 'Not issued to this run',
  'process.refusal.ISSUED_ELSEWHERE': 'Already issued',
  'process.refusal.DUPLICATE': 'Already scanned',
  'process.refusal.NOT_THIS_BATCH': 'Not a sack of this batch',
  'process.refusal.NOT_A_SACK': 'A sack tag is needed here',
  'process.refusal.MACHINE_NO_LABEL': 'This machine has no label yet — label it first',
  'process.refusal.OTHER_MACHINE_LABEL': 'This is the label of {machine} — scan the label on the machine you picked',
  // V1 processing boards (features/process/v1)
  'process.v1.lang': 'en',
  'process.v1.farmerNo': 'No. {no}',
  'process.v1.ordinary': 'Red tie (ordinary)',
  'process.v1.kgUnit': 'kg',
  'process.v1.kg': '{kg} kg',
  'process.v1.tag': 'Tag {no}',
  'process.v1.scanned': 'Scanned',
  'process.v1.weigh.badTag': 'Tag torn or wrong: change it',
  'process.v1.weigh.sackAria': 'Sack {name}, {kg} kg',
  'process.v1.call.dial': 'Call',
  'process.v1.call.noNumber': 'No number',
  'process.v1.call.body': 'Call the quality person on {number}.',
  'process.v1.call.none': 'No quality number on this phone. Tell the supervisor.',
  'process.v1.noPack.title': 'Download the site list',
  'process.v1.noPack.body':
    'This phone has no list of machines and runs yet. Connect to the internet and download it.',
  'process.v1.noPack.download': 'Download',
  'process.v1.noPack.needsNet': 'No internet',
  'process.v1.home.title': 'Today · {date}',
  'process.v1.home.noRun': 'No run open',
  'process.v1.home.runOpen': 'Run …{code} · {machine}',
  'process.v1.home.step1': 'Open run',
  'process.v1.home.step2': 'Clean',
  'process.v1.home.step3': 'Bags in',
  'process.v1.home.step4': 'Run the machine',
  'process.v1.home.step5': 'Weigh, close',
  'process.v1.home.sample': 'Lab sample',
  'process.v1.home.sampleLine': 'Packing batch',
  'process.v1.home.check': 'Check a run',
  'process.v1.home.checkLine': '{count} {count:run|runs} flagged',
  'process.v1.home.open': 'Open run',
  'process.v1.home.continue': 'Continue',
  'process.v1.home.godown': 'Godown',
  'process.v1.home.godownLine': 'Receive, put away, count',
  'process.v1.pick.title': 'Which machine?',
  'process.v1.pick.hint': 'Look at the **machine** and pick',
  'process.v1.pick.busy': 'Run open: …{code}',
  'process.v1.pick.office': 'Phone the office',
  'process.v1.pick.wait': 'Pick the machine',
  'process.v1.pick.none': 'No machine on the site list. Ask the office.',
  'process.v1.lot.title': 'One bag of the lot',
  'process.v1.lot.hint': 'Any one bag',
  'process.v1.lot.stays': 'The bag stays in the godown',
  'process.v1.lot.wait': 'Scan the tag',
  'process.v1.machineScan.title': 'Scan the machine label',
  'process.v1.machineScan.hint': 'The label stuck on this machine',
  'process.v1.machineScan.wait': 'Scan the machine label',
  'process.v1.machineScan.why': 'Stand at the machine and scan its label before the lots',
  'process.v1.machineScan.noLabel': 'This machine has no label yet. Label it before processing on it.',
  'process.v1.machineScan.labelIt': 'Label this machine',
  'process.v1.machineScan.askSupervisor': 'Ask the supervisor to label this machine.',
  'process.v1.machineScan.scanned': 'Label scanned',
  'process.v1.check.title': 'Check',
  'process.v1.check.good': 'Good grain',
  'process.v1.check.red': 'Red tie',
  'process.v1.check.lot': 'Lot …{code}',
  'process.v1.check.fullClean': 'Full clean needed: crop changed',
  'process.v1.check.moreLots': 'More lots',
  'process.v1.check.open': 'Open run',
  'process.v1.cancel.menu': 'Cancel run',
  'process.v1.cancel.title': 'Cancel the run?',
  'process.v1.cancel.line': '{count} {count:bag|bags} in · run …{code}',
  'process.v1.cancel.back': 'The godown puts the bags back in place',
  'process.v1.cancel.yes': 'Yes, cancel',
  'process.v1.cancel.notNow': 'A bag is already in: the run cannot be cancelled',
  'process.v1.clean.title': 'Machine empty',
  'process.v1.clean.opened': 'Run …{code} open',
  'process.v1.clean.full': 'Full clean · crop changed',
  'process.v1.clean.s1': 'Sieves **out**, brush',
  'process.v1.clean.s2': 'Wipe the chute inside',
  'process.v1.clean.s3': 'Whole machine, with air',
  'process.v1.clean.photo': 'Empty hopper, lid open',
  'process.v1.clean.taken': 'Taken',
  'process.v1.clean.take': 'Take photo',
  'process.v1.clean.again': 'Again',
  'process.v1.clean.done': 'Machine empty',
  'process.v1.clean.wait': 'Photo of the empty machine',
  'process.v1.in.title': 'Bag in',
  'process.v1.in.next': 'Next bag',
  'process.v1.in.allTitle': 'All in',
  'process.v1.in.band': '{grade} · in {count} {count:bag|bags}',
  'process.v1.in.bandKg': '{grade} · in {count} {count:bag|bags} · {kg} kg',
  'process.v1.in.thisRun': 'Of this run',
  'process.v1.in.lastWeight': 'Last weight',
  'process.v1.in.s1': 'Into the **hopper**',
  'process.v1.in.s2': '**Cut** the tag',
  'process.v1.in.tipped': 'Tipped in, tag in the tin',
  'process.v1.in.saved': 'Bag {n} in · {kg} kg',
  'process.v1.in.first': 'First bag',
  'process.v1.in.before': 'Before tipping in',
  'process.v1.in.allIn': 'All bags in',
  'process.v1.in.count': '{count} {count:bag|bags} in',
  'process.v1.in.tin': 'Cut tags: {count} in the tin',
  'process.v1.in.wait': 'Tip in one bag first',
  'process.v1.wrong.title': 'Bag will not go in',
  'process.v1.wrong.tag': 'Tag {tag}: {why}',
  'process.v1.wrong.s1': '**Do not** tip it in, do not cut the tag',
  'process.v1.wrong.s2': 'Give it back to the godown',
  'process.v1.wrong.bagOf': "The bag's",
  'process.v1.wrong.runOf': "The run's",
  'process.v1.wrong.ok': 'Got it',
  'process.v1.next': 'Next',
  'process.v1.supervisor': 'Supervisor',
  'process.v1.run.title': 'Machine running',
  'process.v1.run.mixing': 'Other material got in?',
  'process.v1.run.started': 'Started {time}',
  'process.v1.run.tip1': 'Each output in **its own** sack',
  'process.v1.run.tip2': 'Sweepings into waste **at the end**',
  'process.v1.run.weigh': 'Weigh sacks',
  'process.v1.mix.title': 'Other material got in?',
  'process.v1.mix.body': 'The **whole run** turns red tie, its sacks too',
  'process.v1.mix.yes': 'Yes, report',
  'process.v1.mix.reported': 'Other material reported: the run is red tie',
  'process.v1.out.title': 'Which output?',
  'process.v1.out.sacks': '{count} {count:sack|sacks}',
  'process.v1.out.none': 'Nothing came out',
  'process.v1.out.wait': 'Pick the output',
  'process.v1.none.title': 'Which output had nothing?',
  'process.v1.none.zero': '0 kg will be written',
  'process.v1.tag.title': 'Put on a tag',
  'process.v1.tag.band': '{grade} · {name} · sack {n}',
  'process.v1.tag.hint': 'A new tag from the roll',
  'process.v1.tag.fixLast': 'Fix last',
  'process.v1.tag.bad': 'Tag bad',
  'process.v1.tag.done': '{name} done',
  'process.v1.tag.saved': 'Sack {n} · {kg} kg',
  'process.v1.tag.badDone': 'Tag thrown away: put on a new one',
  'process.v1.weigh.title': 'Type the weight',
  'process.v1.weigh.bench': 'Bench scale',
  'process.v1.weigh.label': 'Sack {n} · with the sack',
  'process.v1.weigh.labelWaste': 'With the sack',
  'process.v1.weigh.net': 'Grain {kg}',
  'process.v1.weigh.saveNext': 'Save, next sack',
  'process.v1.weigh.save': 'Save',
  'process.v1.weigh.again': 'Unusual weight: type it again',
  'process.v1.weigh.notSame': 'The two weights differ: type again',
  'process.v1.weigh.tooLight': 'Less than the empty sack',
  'process.v1.weigh.wait': 'Type the weight',
  'process.v1.fix.title': 'Remove the last sack?',
  'process.v1.fix.body': 'Wrong output or weight? Take it off and weigh it again.',
  'process.v1.fix.yes': 'Yes, remove',
  'process.v1.totals.title': 'All weighed?',
  'process.v1.totals.in': 'In',
  'process.v1.totals.out': 'Out',
  'process.v1.totals.more': 'More sacks',
  'process.v1.totals.change': 'Change {name}',
  'process.v1.totals.close': 'Close run',
  'process.v1.totals.missing': '{name}: weigh it or mark nothing',
  'process.v1.close.title': 'Close the run?',
  'process.v1.close.line': '{bags} {bags:bag|bags} in · {sacks} {sacks:sack|sacks} out',
  'process.v1.close.locked': 'After closing, no sack can be added',
  'process.v1.close.yes': 'Yes, close',
  'process.v1.closed.title': 'Run closed',
  'process.v1.closed.place': 'Sacks at the machine area',
  'process.v1.closed.kg': 'In {in} · out {out} kg',
  'process.v1.closed.less': '{kg} kg less: the office will check',
  'process.v1.closed.lessOk': '{kg} kg less: within the limit',
  'process.v1.closed.more': '{kg} kg more out than in: the office will check',
  'process.v1.closed.zero': 'Exactly nothing left over: the office will check',
  'process.v1.closed.sample': 'Take the QC sample',
  'process.v1.closed.home': 'Home',
  'process.v1.qc.title': 'Lab sample',
  'process.v1.qc.batch': '{name} · {count} {count:sack|sacks}',
  'process.v1.qc.batchLine': '{kg} kg · batch …{code} · packing',
  'process.v1.qc.notThese': 'No sample of these',
  'process.v1.qc.none': 'No batch needs a sample now',
  'process.v1.qc.wait': 'Pick the batch',
  'process.v1.scoop.title': 'Scoop from every sack',
  'process.v1.scoop.bag': 'Into a **clean** bag',
  'process.v1.scoop.done': 'Scooped all {count}',
  'process.v1.seal.title': 'Put on the seal',
  'process.v1.seal.mix': 'Mix, fill **to the mark**, close',
  'process.v1.seal.hint': 'Scan the seal',
  'process.v1.seal.scanned': 'Seal {code} scanned',
  'process.v1.seal.bad': 'Bad',
  'process.v1.seal.from': 'Batch …{code} · from {count} {count:sack|sacks}',
  'process.v1.seal.save': 'Save sample',
  'process.v1.seal.done': 'Sample saved',
  'process.v1.seal.doneLine': 'Seal {code} · send it to the lab',
  'process.v1.inv.list': 'Runs to check',
  'process.v1.inv.none': 'No run to check',
  'process.v1.inv.title': 'Check the run',
  'process.v1.inv.held': 'Held',
  'process.v1.inv.notHeld': 'Not held',
  'process.v1.inv.less': '{kg} kg less · limit {pct}',
  'process.v1.inv.more': '{kg} kg more out than in · limit {pct}',
  'process.v1.inv.reweigh': '1. Weigh a doubtful sack again',
  'process.v1.inv.missed': '2. Missed sack: tag, weigh',
  'process.v1.inv.noScan': '3. No scan: tag from the tin',
  'process.v1.inv.nextCause': 'Next: cause',
  'process.v1.inv.own': 'You worked this run: another supervisor must check it.',
  'process.v1.inv.recorded': '{count} {count:change|changes} noted',
  'process.v1.inv.unknown': 'A bag’s weight is not on this phone yet: update the list, then check',
  'process.v1.inv.pouch': '4. Pouch count typed wrong',
  'process.v1.inv.pouchTitle': 'Correct the pouch count',
  'process.v1.inv.pouchNow': 'Now: {count} {count:pouch|pouches}',
  'process.v1.inv.pouchSaved': 'Corrected to {count} {count:pouch|pouches}',
  'process.v1.inv.pouchGood': 'Good pouches',
  'process.v1.inv.pouchSpoiled': 'Spoiled labels',
  'process.v1.inv.pouchDestroyed': 'Labels destroyed',
  'process.v1.inv.pouchMax': 'The kilos tipped in fill at most {max} pouches',
  'process.v1.inv.pouchOver': 'More than {max}: count again',
  'process.v1.inv.pouchWait': 'Type the pouches counted',
  'process.v1.inv.pouchSave': 'Save the count',
  'process.v1.inv.pouchLotOpen':
    'The pack lot is not closed: the pouch count cannot be corrected now',
  'process.v1.rew.title': 'Weigh again',
  'process.v1.rew.missed': 'Missed sack',
  'process.v1.rew.noScan': 'Bag without a scan',
  'process.v1.rew.scanSack': 'Scan the sack',
  'process.v1.rew.scanTin': 'Tag from the tin',
  'process.v1.rew.newTag': 'A new tag from the roll',
  'process.v1.rew.new': 'New weight · with the sack',
  'process.v1.rew.which': 'Which output?',
  'process.v1.cause.title': 'Pick the cause',
  'process.v1.cause.now': 'Short {was} → {now} kg',
  'process.v1.cause.WEIGHT': 'Weight, typing',
  'process.v1.cause.MACHINE': 'Machine, moisture',
  'process.v1.cause.MIXING': 'Other material',
  'process.v1.cause.UNKNOWN': "Don't know",
  'process.v1.cause.which': 'Which?',
  'process.v1.cause.UNWEIGHED_SACK': 'Missed sack',
  'process.v1.cause.SCALE_ERROR': 'Scale wrong',
  'process.v1.cause.TYPING_ERROR': 'Typing wrong',
  'process.v1.cause.BAG_IN_WITHOUT_SCAN': 'No scan',
  'process.v1.cause.HIGH_MOISTURE': 'Moisture',
  'process.v1.cause.MACHINE_FAULT': 'Machine fault',
  'process.v1.cause.weighMore': 'Weigh more',
  'process.v1.cause.fromTin': 'Tag from the tin',
  'process.v1.cause.recheck': 'Re-check',
  'process.v1.cause.wait': 'Pick the cause',
  'process.v1.cause.sent': 'Sent for the re-check',
  'process.v1.cause.accept': 'Accept the loss',
  'process.v1.cause.noAccept':
    'Not a loss to accept here (more came out, or other material): correct the sacks, or the office decides',
} as const

export type ProcessMessageKey = keyof typeof processEn
